import { useState, useEffect, useRef } from 'react';
import { useCXone } from '../contexts/CXoneContext';
import { CallState, AgentState, UnavailableCode } from '../types';
import { logger } from '../utils/logger';
import { resolveIsInAcwState } from './agent-widget/acw-detection.util';
import {
  releasePostCallUiLocks,
  shouldRecoverStuckPostCallState,
} from './agent-widget/post-call-recovery.util';
import { initialAgentState, initialCallState, UseCXoneEventsReturn } from './cxone-events/types';
import { useCxoneCallActions } from './cxone-events/use-cxone-call-actions';
import { CXoneEventsSubscriptionContext } from './cxone-events/subscription-context';
import {
  setupCxoneEventSubscriptions,
  teardownCxoneEventSubscriptions,
} from './cxone-events/subscriptions/setup-subscriptions';
import { resetAgentStateInitialization } from './cxone-events/subscriptions/setup-helpers';

export type { UseCXoneEventsReturn } from './cxone-events/types';

export function useCXoneEvents(): UseCXoneEventsReturn {
  const { isInitialized, audioRef, voicePreference, handleSessionError } = useCXone();

  const [callState, setCallState] = useState<CallState>(initialCallState);
  const [agentState, setAgentStateInternal] = useState<AgentState>(initialAgentState);
  const [unavailableCodes, setUnavailableCodes] = useState<UnavailableCode[]>([]);
  const callStateRef = useRef<CallState>(initialCallState);
  const unavailableCodesRef = useRef<UnavailableCode[]>([]);
  const [isRinging, setIsRinging] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isRecording] = useState(false);
  const [agentLegId, setAgentLegId] = useState<string | null>(null);
  const [agentLegStatus, setAgentLegStatus] = useState<string | null>(null);
  const [isAgentLegActive, setIsAgentLegActive] = useState(false);
  const [isWebRtcConnected, setIsWebRtcConnected] = useState(false);
  const [callError, setCallError] = useState<string | null>(null);
  const [acwTimeout, setAcwTimeout] = useState<number | null>(null);
  const [isInAcw, setIsInAcw] = useState<boolean>(false);

  const pendingAgentLegRef = useRef<string | null>(null);
  const lastContactIdRef = useRef<string | null>(null);
  const agentStateInitializedRef = useRef<boolean>(false);
  const isWebRtcConnectedRef = useRef<boolean>(false);

  const { acceptCall, rejectCall, setAgentState, refreshConferenceParties } = useCxoneCallActions({
    callState,
    unavailableCodes,
    handleSessionError,
    setCallState,
    setAgentStateInternal,
    setIsRinging,
    setCallError,
    setAgentLegId,
    setAgentLegStatus,
    setIsAgentLegActive,
    setIsInAcw,
  });

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  useEffect(() => {
    if (callState.contactId !== lastContactIdRef.current) {
      lastContactIdRef.current = callState.contactId;
    }
  }, [callState.contactId]);

  useEffect(() => {
    isWebRtcConnectedRef.current = isWebRtcConnected;
  }, [isWebRtcConnected]);

  useEffect(() => {
    if (!isInitialized) {
      resetAgentStateInitialization();
      agentStateInitializedRef.current = false;
      return;
    }

    const ctx: CXoneEventsSubscriptionContext = {
      audioRef,
      voicePreference,
      callStateRef,
      unavailableCodesRef,
      pendingAgentLegRef,
      isWebRtcConnectedRef,
      agentStateInitializedRef,
      setCallState,
      setAgentStateInternal,
      setUnavailableCodes,
      setIsRinging,
      setIsMuted,
      setAgentLegId,
      setAgentLegStatus,
      setIsAgentLegActive,
      setIsWebRtcConnected,
      setCallError,
      setAcwTimeout,
      setIsInAcw,
    };

    const subscriptions = setupCxoneEventSubscriptions(ctx);

    return () => {
      teardownCxoneEventSubscriptions(subscriptions, ctx);
    };
  }, [isInitialized, audioRef, voicePreference]);

  useEffect(() => {
    if (
      (callState.status === 'active' || callState.status === 'holding') &&
      isWebRtcConnected &&
      callState.contactId
    ) {
      setCallError(null);
      setIsAgentLegActive(true);
    }
  }, [callState.status, callState.contactId, isWebRtcConnected]);

  useEffect(() => {
    const agentInAcw = resolveIsInAcwState(isInAcw, agentState, unavailableCodes);
    if (
      !shouldRecoverStuckPostCallState({
        callStatus: callState.status,
        agentStateName: agentState.state,
        agentInAcw,
        isRinging,
      })
    ) {
      return;
    }

    logger.lifecycle('[ACW Flow] recovering idle — call state stuck disconnected after call end', {
      agentState: agentState.state,
      agentReason: agentState.reason,
      contactId: callState.contactId,
    });
    setCallState(initialCallState);
    setIsRinging(false);
    setIsAgentLegActive(false);
    setCallError(null);
    releasePostCallUiLocks();
    // Depend on agentState fields used for ACW recovery, not the full agentState object.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- agentState object identity changes frequently
  }, [
    callState.status,
    callState.contactId,
    agentState.state,
    agentState.reason,
    unavailableCodes,
    isInAcw,
    isRinging,
  ]);

  return {
    callState,
    agentState,
    unavailableCodes,
    isRinging,
    isMuted,
    isRecording,
    agentLegId,
    agentLegStatus,
    isAgentLegActive,
    isWebRtcConnected,
    callError,
    acwTimeout,
    isInAcw,
    acceptCall,
    rejectCall,
    setAgentState,
    refreshConferenceParties,
  };
}

export default useCXoneEvents;
